import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useTenant } from "@/contexts/TenantContext";
import FirewallsList from "@/components/FirewallsList";
import ThreatsByTypeChart from "@/components/ThreatsByTypeChart";
import ActiveAlertsTable from "@/components/ActiveAlertsTable";
import BruteForceSimulator from "@/components/BruteForceSimulator";
import { ShieldAlert, ShieldCheck, AlertTriangle, Bug } from "lucide-react";

export default function SOC() {
  const { tenant } = useTenant();
  const [alerts, setAlerts] = useState([]);
  const [reloadKey, setReloadKey] = useState(0);
  const navigate = useNavigate();

  const loadAlerts = useCallback(() => {
    if (!tenant) return;
    api.get(`/soc/active-alerts?tenant=${tenant.id}`).then(({ data }) => setAlerts(data));
  }, [tenant]);

  useEffect(() => { loadAlerts(); }, [loadAlerts, reloadKey]);

  const stats = {
    active: alerts.filter((a) => a.status === "Active").length,
    investigating: alerts.filter((a) => a.status === "Investigating").length,
    mitigated: alerts.filter((a) => a.status === "Mitigated").length,
    critical: alerts.filter((a) => a.severity === "Critical").length,
  };

  return (
    <div className="space-y-6" data-testid="soc-page">
      <div className="border-b border-slate-800 pb-5 flex items-end justify-between gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-slate-500 font-mono mb-2">
            Security Operations · Tier 2
          </div>
          <h1 className="font-display text-4xl font-black tracking-tighter">SOC Console</h1>
          <p className="text-sm text-slate-400 mt-1">
            Federated firewalls · SIEM · EDR · IAM · threat intelligence
          </p>
        </div>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <SocStat icon={ShieldAlert} label="Active" value={stats.active} color="#EF4444" testid="soc-stat-active" />
        <SocStat icon={AlertTriangle} label="Investigating" value={stats.investigating} color="#F59E0B" testid="soc-stat-investigating" />
        <SocStat icon={ShieldCheck} label="Mitigated" value={stats.mitigated} color="#10B981" testid="soc-stat-mitigated" />
        <SocStat icon={Bug} label="Critical Severity" value={stats.critical} color="#A855F7" testid="soc-stat-critical" />
      </div>

      {/* Row: Threats chart + Firewalls list */}
      <div className="grid grid-cols-1 xl:grid-cols-12 gap-4">
        <div className="xl:col-span-5 border border-slate-800 bg-[#0F172A]">
          <div className="border-b border-slate-800 px-5 py-3 flex items-center justify-between">
            <div>
              <h3 className="text-xs uppercase tracking-[0.25em] font-mono font-semibold">Threats Blocked by Type</h3>
              <div className="text-[10px] text-slate-500 mt-0.5">Last 7 days · across all federated firewalls</div>
            </div>
          </div>
          <ThreatsByTypeChart />
        </div>

        <div className="xl:col-span-7 border border-slate-800 bg-[#0F172A]">
          <div className="border-b border-slate-800 px-5 py-3 flex items-center justify-between">
            <div>
              <h3 className="text-xs uppercase tracking-[0.25em] font-mono font-semibold">Security Gateways · Firewalls</h3>
              <div className="text-[10px] text-slate-500 mt-0.5">Status, uptime, policy version per remote unit</div>
            </div>
          </div>
          <FirewallsList />
        </div>
      </div>

      {/* Row: Brute-force simulator (Security Incident Simulator) + Active alerts */}
      <div className="grid grid-cols-1 xl:grid-cols-12 gap-4">
        <div className="xl:col-span-4 border border-red-500/30 bg-gradient-to-br from-[#0F172A] to-[#1c1014]">
          <div className="border-b border-red-500/30 px-5 py-3 flex items-center gap-2">
            <Bug className="w-4 h-4 text-red-400" />
            <h3 className="text-xs uppercase tracking-[0.25em] font-mono font-semibold">Security Incident Simulator</h3>
          </div>
          <BruteForceSimulator onMitigated={() => setReloadKey((k) => k + 1)} />
        </div>

        <div className="xl:col-span-8 border border-slate-800 bg-[#0F172A]">
          <div className="border-b border-slate-800 px-5 py-3 flex items-center justify-between">
            <div>
              <h3 className="text-xs uppercase tracking-[0.25em] font-mono font-semibold">Active Security Alerts</h3>
              <div className="text-[10px] text-slate-500 mt-0.5">Triage queue · click an alert to mitigate</div>
            </div>
            <span className="text-[10px] font-mono text-emerald-400 evos-blink">● LIVE</span>
          </div>
          <ActiveAlertsTable alerts={alerts} onMitigated={() => setReloadKey((k) => k + 1)} />
        </div>
      </div>
    </div>
  );
}

function SocStat({ icon: Icon, label, value, color, testid }) {
  return (
    <div className="border border-slate-800 bg-[#0F172A] p-5 flex items-start gap-4" data-testid={testid}>
      <div className="w-9 h-9 flex items-center justify-center border flex-shrink-0" style={{ borderColor: color + "55", background: color + "12" }}>
        <Icon className="w-4 h-4" style={{ color }} />
      </div>
      <div>
        <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-slate-500">{label}</div>
        <div className="text-3xl font-mono font-bold mt-1" style={{ color }}>{value}</div>
      </div>
    </div>
  );
}
